import React from "react"
import {Box, Link, Tooltip} from "@mui/material"
import type {SxProps, Theme} from "@mui/material"
import ManageSearchIcon from "@mui/icons-material/ManageSearch"

// Shared entry point into advanced search. Rendered on the customers list page and on
// the customer/account detail page. This component is the single owner of the
// VITE_ENABLE_ADVANCED_SEARCH_UI gate — callers must NOT re-check the flag. The caller
// supplies onClick (toggle the panel, or navigate to it) and an optional wrapperSx so the
// list page can position it without an empty wrapper rendering when the flag is off.
function AdvancedSearchEntryLink({onClick, wrapperSx}: {onClick: () => void; wrapperSx?: SxProps<Theme>}) {
	const enabled = import.meta.env.VITE_ENABLE_ADVANCED_SEARCH_UI === "true"
	if (!enabled) return null

	const link = (
		<Tooltip
			describeChild
			arrow
			placement="right"
			title="Combine several details to find a customer — including phone numbers and emails they've removed."
		>
			<Link
				component="button"
				type="button"
				onClick={onClick}
				underline="hover"
				sx={{
					display: "inline-flex",
					alignItems: "center",
					gap: 0.5,
					fontSize: "0.95rem",
					fontWeight: 600,
					color: "primary.main",
				}}
			>
				<ManageSearchIcon fontSize="small" />
				<Box
					component="span"
					sx={(theme) => ({
						backgroundImage: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.main} 40%, ${theme.palette.primary.light} 50%, ${theme.palette.primary.main} 60%, ${theme.palette.primary.main} 100%)`,
						backgroundSize: "200% auto",
						backgroundClip: "text",
						WebkitBackgroundClip: "text",
						WebkitTextFillColor: "transparent",
						animation: "advancedSearchShine 4s ease-in-out infinite",
						"@keyframes advancedSearchShine": {
							"0%": {backgroundPosition: "200% center"},
							"55%, 100%": {backgroundPosition: "-200% center"},
						},
						"@media (prefers-reduced-motion: reduce)": {
							animation: "none",
							backgroundImage: "none",
							WebkitTextFillColor: theme.palette.primary.main,
						},
					})}
				>
					or use advanced search →
				</Box>
			</Link>
		</Tooltip>
	)

	return wrapperSx ? <Box sx={wrapperSx}>{link}</Box> : link
}

export {AdvancedSearchEntryLink}
